import { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { Button, RouterButtonLink } from "../components/Button";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { GiftcardForm } from "./components/GiftcardForm";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import { LoadMoreButton } from "../components/LoadMoreButton";
import {
  useGetGiftcardsInfiniteQuery,
  useCreateGiftcardMutation,
  useUpdateGiftcardMutation,
  useDeleteGiftcardMutation,
} from "../query/giftcardsQuery";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useGetStoreQuery } from "../query/storesQuery";
import { useIsAdmin, useIsReseller } from "../hooks/useIsAdmin";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatDate } from "../utils/dateUtils";
import type { RestGiftCard, RestCreateGiftCard } from "@KassaroAB/shared/types/giftCard";
import type { GiftCardId, StoreId } from "@KassaroAB/shared/types/ids";
import { formatMoney } from "../utils/formatMoney";

const DeleteButton = styled(Button)`
  background-color: ${colors.danger};
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const StoreNameCell = ({ storeId }: { storeId: StoreId }) => {
  const { data: store } = useGetStoreQuery(storeId);
  return (
    <TableCell noWrap>{store?.displayName || store?.name || storeId}</TableCell>
  );
};

export const GiftcardsView = () => {
  const { t } = useTranslation();
  const { scope, storeId } = useCatalogScope();
  const [selectedGiftcardId, setSelectedGiftcardId] = useState<GiftCardId | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isAdmin = useIsAdmin();
  const isReseller = useIsReseller();
  const canEdit = Boolean(storeId) && (isAdmin || isReseller);

  const {
    data: giftcardsData,
    isLoading: isLoadingGiftcards,
    error: giftcardsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetGiftcardsInfiniteQuery(scope);

  const giftcards = giftcardsData?.pages.flatMap((page) => page) ?? [];
  const isChain = scope?.type === "chain";

  const { mutate: createMutation, error: createError, isPending: isCreatePending } =
    useCreateGiftcardMutation(storeId ?? null);
  const { mutate: updateMutation, error: updateError, isPending: isUpdatePending } =
    useUpdateGiftcardMutation(storeId ?? null);
  const { mutate: deleteMutation, error: deleteError } =
    useDeleteGiftcardMutation(storeId ?? null);

  const selectedGiftcard = giftcards?.find((g) => g.id === selectedGiftcardId);

  const handleGiftcardClick = (giftcard: RestGiftCard) => {
    if (!canEdit) return;
    setSelectedGiftcardId(giftcard.id);
    setIsCreating(false);
    setIsPanelOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedGiftcardId(null);
    setIsCreating(true);
    setIsPanelOpen(true);
  };

  const handleCreate = (data: RestCreateGiftCard) => {
    createMutation(data, {
      onSuccess: () => {
        setSuccessMessage(t("giftcardsView.successCreated"));
        setIsPanelOpen(false);
        setIsCreating(false);
        setSelectedGiftcardId(null);
      },
      onError: () => {
        setSuccessMessage(null);
      },
    });
  };

  const handleUpdate = (data: RestCreateGiftCard) => {
    if (!selectedGiftcardId) return;
    updateMutation(
      {
        giftcardId: selectedGiftcardId,
        giftcard: data,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t("giftcardsView.successUpdated"));
          setIsPanelOpen(false);
          setSelectedGiftcardId(null);
        },
        onError: () => {
          setSuccessMessage(null);
        },
      },
    );
  };

  const handleDelete = (giftcardId: GiftCardId, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("giftcardsView.deleteConfirm"))) {
      deleteMutation(giftcardId, {
        onSuccess: () => {
          setSuccessMessage(t("giftcardsView.successDeleted"));
        },
        onError: () => {
          setSuccessMessage(null);
        },
      });
    }
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedGiftcardId(null);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoadingGiftcards && giftcards.length === 0) {
    return (
      <PageContent title={t("giftcardsView.title")}>
        <LoadingContainer message={t("giftcardsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("giftcardsView.title")}
      actions={
        canEdit ? (
          <>
            {storeId && (
              <RouterButtonLink
                to={`/stores/${storeId}/giftcards/import`}
                $color="secondary"
              >
                {t("giftcardsView.import")}
              </RouterButtonLink>
            )}
            <Button onClick={handleCreateClick}>{t("giftcardsView.create")}</Button>
          </>
        ) : undefined
      }
    >
      {giftcardsError && (
        <InfoBox variant="error">
          {t("giftcardsView.errorLoad")}
        </InfoBox>
      )}

      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">
          {t("giftcardsView.errorSave")}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {giftcards.length > 0 ? (
        <>
          <Card>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>{t("giftcardsView.tableCardNumber")}</TableHeaderCell>
                  {isChain && (
                    <TableHeaderCell>{t("giftcardsView.tableStore")}</TableHeaderCell>
                  )}
                  <TableHeaderCell>{t("giftcardsView.tableInitialAmount")}</TableHeaderCell>
                  <TableHeaderCell>{t("giftcardsView.tableRemainingAmount")}</TableHeaderCell>
                  <TableHeaderCell>{t("giftcardsView.tableValidUntil")}</TableHeaderCell>
                  <TableHeaderCell>{t("giftcardsView.tableCreated")}</TableHeaderCell>
                  {canEdit && <TableHeaderCell>{t("giftcardsView.tableActions")}</TableHeaderCell>}
                </tr>
              </TableHeader>
              <TableBody>
                {giftcards.map((giftcard) => (
                  <TableRow
                    key={giftcard.id}
                    clickable={canEdit}
                    onClick={() => handleGiftcardClick(giftcard)}
                  >
                    <TableCell noWrap>{giftcard.cardNumber}</TableCell>
                    {isChain && <StoreNameCell storeId={giftcard.storeId} />}
                    <TableCell noWrap>
                      {formatMoney(giftcard.initialAmount)}
                    </TableCell>
                    <TableCell noWrap>
                      {formatMoney(giftcard.remainingAmount)}
                    </TableCell>
                    <TableCell noWrap>{formatDate(giftcard.validUntil)}</TableCell>
                    <TableCell noWrap>{formatDate(giftcard.createdAt)}</TableCell>
                    {canEdit && (
                      <TableCell noWrap>
                        <DeleteButton onClick={(e) => handleDelete(giftcard.id, e)}>
                          {t("giftcardsView.delete")}
                        </DeleteButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <LoadMoreButton
            onClick={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
            hasMore={hasNextPage}
          />
        </>
      ) : !isLoadingGiftcards ? (
        <Card>
          <div style={{ textAlign: "center", color: colors.textMuted }}>
            {t("giftcardsView.empty")}
            {canEdit && ` ${t("giftcardsView.emptyHint")}`}
          </div>
        </Card>
      ) : null}

      {canEdit && (selectedGiftcard || isCreating) && (
        <SidebarPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          title={
            isCreating
              ? t("giftcardsView.panelCreate")
              : t("giftcardsView.panelEdit", { number: selectedGiftcard?.cardNumber ?? "" })
          }
        >
          <GiftcardForm
            giftcard={selectedGiftcard || undefined}
            onSubmit={selectedGiftcard ? handleUpdate : handleCreate}
            onCancel={handlePanelClose}
            isLoading={isCreatePending || isUpdatePending}
          />
        </SidebarPanel>
      )}
    </PageContent>
  );
};
